import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from "react";

/**
 * Footer visibility, driven by what is actually mounted: every live call screen holds a
 * suppression while mounted, and the footer shows only when none is held. Reference-counted
 * with idempotent release, so React StrictMode's mount→unmount→mount, overlapping screens and
 * navigation all settle correctly. Pure (no DOM) + unit-tested.
 */
export class FooterSuppression {
  private holds = 0;
  private readonly listeners = new Set<() => void>();

  /** Suppress the footer until the returned release is called (calling it twice is a no-op). */
  acquire(): () => void {
    this.holds++;
    this.emit();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.holds--;
      this.emit();
    };
  }

  get visible(): boolean {
    return this.holds === 0;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit(): void {
    for (const l of this.listeners) l();
  }
}

const FooterContext = createContext<FooterSuppression>(new FooterSuppression());

export function FooterVisibilityProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => new FooterSuppression());
  return <FooterContext.Provider value={store}>{children}</FooterContext.Provider>;
}

/** Hide the app footer for as long as the calling component is mounted (the live call screen). */
export function useHideFooterWhileMounted(): void {
  const store = useContext(FooterContext);
  useEffect(() => store.acquire(), [store]);
}

/**
 * Shared footer, rendered once below the active shell (never inside a shell, so it cannot be
 * duplicated) as a normal flex child — not an overlay. Absent while a call screen is mounted,
 * so it never covers call controls or takes space from the video stage. Always physical
 * left/right (dir="ltr") with the provided English text in every locale.
 */
export function AppFooter() {
  const store = useContext(FooterContext);
  const visible = useSyncExternalStore(store.subscribe, () => store.visible, () => true);
  if (!visible) return null;
  return (
    <footer
      className="app-footer"
      dir="ltr"
      style={{
        flex: "0 0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
        gap: "4px 16px", padding: "6px 18px", minHeight: 24, fontSize: 12, lineHeight: 1.4,
        color: "#64748b", background: "#f8fafc", borderTop: "1px solid #e2e8f0",
        fontFamily: "Segoe UI, Arial, sans-serif"
      }}
    >
      <span>© All Rights Reserved to Whisperz Systems</span>
      <a className="footer__powered-by" href="https://www.igates.co.il/"
        style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto", color: "#475569", textDecoration: "none" }}>
        <img alt="" src="/figma/igates-site-mark-48.png" width={20} height={20} style={{ display: "block" }} />
        <span>Powered by iGATES</span>
      </a>
    </footer>
  );
}
